import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IPanelNotification } from "@/store/types/notification";
import { getPanelNotifications, markAllNotificationsAsRead, markNotificationAsRead, PanelNotification } from "./notificationApi";

interface NotificationState {
  notifications: IPanelNotification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
}

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  loading: false,
  error: null,
};

const notificationSlice = createSlice({
  name: "panelNotification",
  initialState,
  reducers: {
    setNotificationsFromRealtime(
      state,
      action: PayloadAction<IPanelNotification[]>
    ) {
      state.notifications = action.payload;
      state.unreadCount = action.payload.filter(
        (n) => !n.isRead
      ).length;
    },

    resetNotifications: () => initialState,
  },
  extraReducers: (builder) => {
    // =========================
    // FETCH NOTIFICATIONS
    // =========================
    builder
      .addCase(getPanelNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getPanelNotifications.fulfilled,
        (state, action: PayloadAction<IPanelNotification[]>) => {
          state.loading = false;
          state.notifications = action.payload;
          state.unreadCount = action.payload.filter(
            (n) => !n.isRead,
          ).length;
        },
      )
      .addCase(getPanelNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || 'Failed to fetch notifications';
      });

    // =========================
    // MARK SINGLE AS READ
    // =========================
    builder
      .addCase(markNotificationAsRead.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        markNotificationAsRead.fulfilled,
        (state, action) => {
          state.loading = false;

          const notification = state.notifications.find(
            (n) => n.id === action.payload.notificationId,
          );

          if (notification && !notification.isRead) {
            notification.isRead = true;
            state.unreadCount = Math.max(state.unreadCount - 1, 0);
          }
        },
      )
      .addCase(markNotificationAsRead.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || 'Failed to mark notification as read';
      });

    // =========================
    // MARK ALL AS READ
    // =========================
    builder
      .addCase(markAllNotificationsAsRead.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(markAllNotificationsAsRead.fulfilled, (state) => {
        state.loading = false;
        state.notifications = state.notifications.map((n) => ({
          ...n,
          isRead: true,
        }));
        state.unreadCount = 0;
      })
      .addCase(markAllNotificationsAsRead.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload ||
          'Failed to mark all notifications as read';
      });
  },
});

export const {
  setNotificationsFromRealtime,
  resetNotifications,
} = notificationSlice.actions;

export default notificationSlice.reducer;
